// DELETE-shape extractor — pulls deleteTarget / FROM-tail (PG USING, MSSQL
// JOIN form) / WHERE / RETURNING.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import { scanTopLevelKeywords } from './scanner';
import type { SelectExtractCtx } from './extractQueryStructure.select';
import { parseFromAndJoins } from './extractQueryStructure.from';
import { parseBooleanFragments } from './extractQueryStructure.predicates';
import { parseSelectItems } from './extractQueryStructure.selectList';
import {
  pickFirstStart, readDmlTarget,
  type MutableQueryStructure,
} from './extractQueryStructure.dml.shared';

const JOIN_PREFIX_KEYWORDS = new Set([
  'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER',
  'STRAIGHT_JOIN', 'NATURAL',
]);

// eslint-disable-next-line complexity
export function extractDeleteShape(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): MutableQueryStructure {
  const { source, options } = ctx;
  const kws = scanTopLevelKeywords(source, start, end, options);
  const deleteIdx = kws.findIndex((k) => k.upper === 'DELETE');
  if (deleteIdx < 0) return {};
  const fromIdx = kws.findIndex((k, i) => i > deleteIdx && k.upper === 'FROM');
  if (fromIdx < 0) return {};
  const target = readDmlTarget(ctx, kws[fromIdx].end, end);
  if (!target) return {};

  const usingIdx = kws.findIndex((k, i) => i > fromIdx && k.upper === 'USING');
  const whereIdx = kws.findIndex((k, i) => i > fromIdx && k.upper === 'WHERE');
  const returningIdx = kws.findIndex((k, i) => i > fromIdx && k.upper === 'RETURNING');

  const out: MutableQueryStructure = { deleteTarget: target.item };

  // MSSQL multi-table form: `DELETE … FROM t JOIN u …`. If a JOIN-prefix
  // keyword appears after the target but before WHERE/RETURNING, walk the
  // segment as a FROM tail.
  const joinPrefixIdx = kws.findIndex((k, i) => i > fromIdx && JOIN_PREFIX_KEYWORDS.has(k.upper));

  let tailStart = -1;
  if (usingIdx >= 0) tailStart = kws[usingIdx].end;
  else if (joinPrefixIdx >= 0) tailStart = target.endOffset;

  if (tailStart >= 0) {
    const tailEnd = pickFirstStart(kws, [whereIdx, returningIdx], end);
    const split = parseFromAndJoins(ctx, tailStart, tailEnd);
    if (split.fromItems.length > 0) out.fromItems = split.fromItems;
    if (split.joins.length > 0) out.joins = split.joins;
  }

  if (whereIdx >= 0) {
    const whereEnd = pickFirstStart(kws, [returningIdx], end);
    const where = parseBooleanFragments(ctx, kws[whereIdx].end, whereEnd);
    if (where) out.where = where;
  }
  if (returningIdx >= 0) {
    const ret = parseSelectItems(ctx, kws[returningIdx].end, end);
    if (ret.length > 0) out.returning = ret;
  }
  return out;
}
